import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Stack,
  Typography,
  Snackbar,
  SwipeableDrawer,
  Tabs,
  Tab,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import CloseRounded from "@mui/icons-material/CloseRounded";
import AttachMoneyRounded from "@mui/icons-material/AttachMoneyRounded";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import MoreHorizRounded from "@mui/icons-material/MoreHorizRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import IosShareRounded from "@mui/icons-material/IosShareRounded";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";

import type { MarketplaceTemplate } from "@/data/mock-data";
import { EmptyState } from "@/components/EmptyState";
import { BotAcquireFlow } from "@/components/BotAcquireFlow";
import { BacktestChart } from "@/components/BacktestChart";
import { syntheticBacktestCurve } from "@/lib/backtest-chart";
import { desktopBottomDrawerPaperSx } from "@/theme/drawer-sx";
import { useCanShareBot } from "@/lib/features";
import { useBotActionsStore } from "@/store/bot-actions-store";

/** Copy text to the clipboard, swallowing the rare rejection. */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// ---- Props -----------------------------------------------------------------

interface MarketplaceDetailDrawerProps {
  /** Non-null → drawer open showing this template. */
  template: MarketplaceTemplate | null;
  onClose: () => void;
}

type MpTab = "backtest" | "structure" | "about";

const MP_TABS: ReadonlyArray<{ key: MpTab; label: string }> = [
  { key: "backtest", label: "Backtest" },
  { key: "structure", label: "Algo" },
  { key: "about", label: "About" }
];

// ---- Component -------------------------------------------------------------

/**
 * Marketplace bot detail — bottom-sheet drawer. Backtest / Structure
 * / About tabs with a Buy / Watch / ⋯ action bar. "Watch" buys the
 * bot on a paper account so the user can track it before committing
 * real capital.
 *
 * Fixed 92vh height so switching tabs doesn't make the sheet jump —
 * same treatment as BotDetailDrawer.
 */
export function MarketplaceDetailDrawer({
  template,
  onClose
}: MarketplaceDetailDrawerProps) {
  // Keep the last template through the slide-out animation.
  const [snapshot, setSnapshot] = useState<MarketplaceTemplate | null>(
    template
  );
  if (template && template !== snapshot) setSnapshot(template);

  const [tab, setTab] = useState<MpTab>("backtest");
  const [openedId, setOpenedId] = useState<string | null>(
    template?.id ?? null
  );
  if (template && template.id !== openedId) {
    setOpenedId(template.id);
    setTab("backtest");
  }
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  // `watch: true` adds a "Go to My Watch List" link to the toast.
  const [toast, setToast] = useState<{ msg: string; watch?: boolean } | null>(
    null
  );
  const [acquireOpen, setAcquireOpen] = useState(false);
  const canShare = useCanShareBot();
  const navigate = useNavigate();

  const watchedIds = useBotActionsStore((s) => s.watchedIds);
  const toggleWatched = useBotActionsStore((s) => s.toggleWatched);

  const t = snapshot;
  if (!t) return null;

  const isWatched = watchedIds.includes(t.id);

  async function handleShare() {
    setMoreAnchor(null);
    if (!t) return;
    const ok = await copyToClipboard(
      `https://app.whitehatbots.com/marketplace/${t.id}`
    );
    setToast({
      msg: ok
        ? "Marketplace link copied to clipboard."
        : "Couldn't access the clipboard — copy the link manually."
    });
  }

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={template !== null}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      disableDiscovery
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          pb: "env(safe-area-inset-bottom)",
          height: "92vh",
          display: "flex",
          flexDirection: "column",
          ...desktopBottomDrawerPaperSx
        }
      }}
    >
      {/* Grab handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
        <Box
          sx={(th: Theme) => ({
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: th.palette.divider
          })}
        />
      </Box>

      {/* Close (×) */}
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={(th: Theme) => ({
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
          bgcolor: alpha(th.palette.text.primary, 0.08),
          "&:hover": { bgcolor: alpha(th.palette.text.primary, 0.16) }
        })}
      >
        <CloseRounded sx={{ fontSize: 22 }} />
      </IconButton>

      {/* Header */}
      <Stack spacing={0.5} sx={{ px: 2, pt: 1, pb: 1.5, pr: 7 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {t.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          By {t.creator}
        </Typography>
      </Stack>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v: MpTab) => setTab(v)}
        variant="fullWidth"
        sx={{
          borderTop: 1,
          borderBottom: 1,
          borderColor: "divider",
          minHeight: 42,
          flexShrink: 0,
          "& .MuiTab-root": {
            minHeight: 42,
            fontWeight: 600,
            fontSize: "0.9375rem",
            textTransform: "none"
          }
        }}
      >
        {MP_TABS.map((x) => (
          <Tab key={x.key} value={x.key} label={x.label} />
        ))}
      </Tabs>

      {/* Scrollable content */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {tab === "backtest" && <MpBacktest template={t} />}
        {tab === "structure" && (
          <EmptyState
            icon={<AccountTreeOutlined />}
            title="Algo"
            body="The IF/ELSE rule tree behind this bot — the visual viewer arrives with the bot builder."
          />
        )}
        {tab === "about" && <MpAbout template={t} />}
      </Box>

      <Divider />

      {/* Action bar — Buy / Watch / ⋯ */}
      <Box sx={{ p: 1.5, flexShrink: 0 }}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AttachMoneyRounded sx={{ fontSize: 18 }} />}
            onClick={() => setAcquireOpen(true)}
            sx={{ flex: 1, fontWeight: 700 }}
          >
            Buy
          </Button>
          <Button
            variant={isWatched ? "contained" : "outlined"}
            color="primary"
            startIcon={
              isWatched ? (
                <VisibilityOffOutlined sx={{ fontSize: 18 }} />
              ) : (
                <VisibilityOutlined sx={{ fontSize: 18 }} />
              )
            }
            onClick={() => {
              toggleWatched(t.id);
              setToast(
                isWatched
                  ? { msg: `Stopped watching ${t.name}.` }
                  : {
                      msg: `Now watching "${t.name}" in Marketplace.`,
                      watch: true
                    }
              );
            }}
            sx={{ flex: 1, fontWeight: 700 }}
          >
            {isWatched ? "Unwatch" : "Watch"}
          </Button>
          <IconButton
            aria-label="More actions"
            onClick={(e) => setMoreAnchor(e.currentTarget)}
            sx={(th) => ({
              border: `1px solid ${th.palette.divider}`,
              borderRadius: 1.5
            })}
          >
            <MoreHorizRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
      </Box>

      <Menu
        open={Boolean(moreAnchor)}
        anchorEl={moreAnchor}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setMoreAnchor(null);
            setToast({ msg: `Saved a draft copy of ${t.name}.` });
          }}
        >
          <ListItemIcon><ContentCopyRounded fontSize="small" /></ListItemIcon>
          <ListItemText>Copy to drafts</ListItemText>
        </MenuItem>
        {canShare && (
          <MenuItem onClick={handleShare}>
            <ListItemIcon><IosShareRounded fontSize="small" /></ListItemIcon>
            <ListItemText>Share</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Buy flow — multi-step: invest amount → wizard settings (wizard
       *  templates only) → assign broker → run. */}
      <BotAcquireFlow
        open={acquireOpen}
        template={t}
        onClose={() => setAcquireOpen(false)}
        onDone={(msg) => setToast({ msg })}
      />

      {/* Action acknowledgement toast. Watch toasts carry a "Go to My
       *  Watch List" link that deep-links to the Marketplace watch
       *  segment (Sergey 2026-05-21). */}
      <Snackbar
        open={toast !== null}
        autoHideDuration={toast?.watch ? 7000 : 4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ zIndex: (th) => th.zIndex.modal + 6 }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setToast(null)}
          sx={{ width: "100%" }}
          action={
            toast?.watch ? (
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  setToast(null);
                  onClose();
                  navigate("/marketplace?view=watch");
                }}
                sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
              >
                Go to My Watch List
              </Button>
            ) : undefined
          }
        >
          {toast?.msg}
        </Alert>
      </Snackbar>
    </SwipeableDrawer>
  );
}

// ---- Backtest tab ----------------------------------------------------------

function MpBacktest({ template: t }: { template: MarketplaceTemplate }) {
  // Graph / Performance sub-tabs — the Graph tab shows the full
  // account-style chart (Sergey 2026-05-21). The simulated curve is a
  // deterministic 1-year walk ending at the template's cumulative
  // return.
  const [sub, setSub] = useState<"graph" | "performance">("graph");
  const curve = useMemo(
    () => syntheticBacktestCurve(t.id, t.cumulativeReturn, 365),
    [t.id, t.cumulativeReturn]
  );
  const rows: { label: string; value: string; tone?: "pos" | "neg" }[] = [
    {
      label: "Cumulative Return",
      value: `+${t.cumulativeReturn.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`,
      tone: "pos"
    },
    {
      label: "Annualized Return",
      value: `+${t.ar.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`,
      tone: "pos"
    },
    { label: "Max Drawdown", value: `−${t.maxDrawdown.toFixed(1)}%`, tone: "neg" },
    { label: "Sharpe Ratio", value: t.sharpe.toFixed(2) },
    { label: "Calmar Ratio", value: t.calmar.toFixed(2) },
    { label: "Standard Deviation", value: `${t.stdDev.toFixed(1)}%` },
    {
      label: "Trailing 1M Return",
      value: `${t.trailing1M >= 0 ? "+" : "−"}${Math.abs(t.trailing1M).toFixed(1)}%`,
      tone: t.trailing1M >= 0 ? "pos" : "neg"
    },
    {
      label: "Trailing 3M Return",
      value: `${t.trailing3M >= 0 ? "+" : "−"}${Math.abs(t.trailing3M).toFixed(1)}%`,
      tone: t.trailing3M >= 0 ? "pos" : "neg"
    },
    {
      label: "Recovery Time",
      value: `${t.recoveryTimeMonths} month${t.recoveryTimeMonths === 1 ? "" : "s"}`
    }
  ];
  return (
    <Box>
      {/* Graph / Performance sub-tab strip */}
      <Stack direction="row" spacing={1} sx={{ px: 2, pt: 1.5 }}>
        {(
          [
            { key: "graph", label: "Graph" },
            { key: "performance", label: "Performance" }
          ] as const
        ).map((s) => (
          <Chip
            key={s.key}
            label={s.label}
            variant={sub === s.key ? "filled" : "outlined"}
            onClick={() => setSub(s.key)}
            sx={{
              fontWeight: 700,
              // The theme forces every filled chip to a dark navy bg —
              // `color="primary"` alone leaves dark text on dark. Force
              // the mint bg + contrast text for the active chip.
              ...(sub === s.key
                ? {
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": { bgcolor: "primary.light" }
                  }
                : {})
            }}
          />
        ))}
      </Stack>

      {sub === "graph" ? (
        <BacktestChart id={t.id} curve={curve} />
      ) : (
        <Box sx={{ px: 2, py: 2 }}>
          <Typography
            variant="overline"
            sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.5 }}
          >
            Simulated Performance
          </Typography>
          <Box sx={{ mt: 1 }}>
            {rows.map((r, i) => (
              <Box key={r.label}>
                {i > 0 && <Divider />}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 1.25 }}
                >
                  <Typography variant="body1" color="text.secondary">
                    {r.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={(th) => ({
                      fontWeight: 700,
                      fontFeatureSettings: '"tnum" 1',
                      color:
                        r.tone === "pos"
                          ? th.palette.success.main
                          : r.tone === "neg"
                            ? th.palette.error.main
                            : th.palette.text.primary
                    })}
                  >
                    {r.value}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 2 }}
          >
            Backtest results are simulated and not reflective of actual
            performance.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ---- About tab -------------------------------------------------------------

function MpAbout({ template: t }: { template: MarketplaceTemplate }) {
  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Stack spacing={2}>
        <Field label="Description" value={t.description} />
        <Field label="Created by" value={t.creator} />
        <Field label="Assets" value={t.assets} />
        <Field label="Recent OOS date" value={t.oosDate} />
        <Field label="Listed" value={t.listedDate} />
        <Box>
          <Typography variant="caption" color="text.secondary">
            Categories
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: "wrap", gap: 1, mt: 0.5 }}
          >
            {t.tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" variant="outlined" />
            ))}
          </Stack>
        </Box>
      </Stack>
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          borderRadius: 1.5,
          bgcolor: "action.hover",
          display: "flex",
          gap: 1.25,
          alignItems: "flex-start"
        }}
      >
        <StorefrontOutlined sx={{ fontSize: 20, color: "text.secondary" }} />
        <Typography variant="caption" color="text.secondary">
          Buying adds this bot to your account. Use “Watch” to track it
          on a paper account first.
        </Typography>
      </Box>
    </Box>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );
}
